import { IconClose } from "@arco-design/web-react/icon";
import { Button } from "@arco-design/web-react";
import axios from "axios";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { endpoints } from "../../api/endpoints";
import { routes } from "../../routes/routes";
import { RootState } from "../../store/store";
import { getButtonColor, getButtonTextColor } from "../../utils/cssVariables";

import "./MentorAvailabilityReminder.css";

function shouldShowMentorAvailabilityReminder(
  user: { id?: number; isMentor?: boolean; isAdmin?: boolean } | null,
  isCalendlyLinkRequired: boolean,
  aiMatchingPreview: boolean,
): boolean {
  const usePreviewNav = aiMatchingPreview && !user?.isAdmin;
  return Boolean(
    user?.id && user.isMentor && !user.isAdmin && !isCalendlyLinkRequired && !usePreviewNav,
  );
}

export function MentorAvailabilityReminder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.user.user);
  const configLoaded = useSelector((state: RootState) => state.config.configLoaded);
  const isCalendlyLinkRequired = useSelector(
    (state: RootState) => state.config.isCalendlyLinkRequired,
  );
  const aiMatchingPreview = useSelector(
    (state: RootState) => state.config.aiMatchingPreview,
  );

  const [dismissed, setDismissed] = useState(false);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [hasSlots, setHasSlots] = useState(false);

  const eligible = shouldShowMentorAvailabilityReminder(
    user,
    isCalendlyLinkRequired,
    aiMatchingPreview,
  );
  const onDashboard = location.pathname === routes.DASHBOARD;

  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!eligible || !onDashboard || !user?.id) {
      setSlotsLoaded(false);
      setHasSlots(false);
      return;
    }

    let cancelled = false;
    setSlotsLoaded(false);

    axios
      .get(endpoints.getSlots)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const slots = response.data;
        setHasSlots(Array.isArray(slots) && slots.length > 0);
        setSlotsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHasSlots(false);
          setSlotsLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eligible, onDashboard, user?.id, location.pathname]);

  const visible =
    configLoaded && eligible && onDashboard && slotsLoaded && !hasSlots && !dismissed;

  if (!visible) {
    return null;
  }

  return createPortal(
    <aside
      className="mentor-availability-reminder"
      role="dialog"
      aria-labelledby="mentor-availability-reminder-title"
      aria-live="polite"
    >
      <button
        type="button"
        className="mentor-availability-reminder__close"
        aria-label={t("mentor_availability_reminder.close_label")}
        onClick={() => setDismissed(true)}
      >
        <IconClose />
      </button>

      <h2 id="mentor-availability-reminder-title" className="mentor-availability-reminder__title">
        {t("mentor_availability_reminder.title")}
      </h2>

      <p className="mentor-availability-reminder__body">
        {t("mentor_availability_reminder.body")}
      </p>

      <Button
        htmlType="button"
        className="mentor-availability-reminder__cta"
        size="large"
        shape="round"
        onClick={() => navigate(routes.MENTORSETTINGS)}
        style={{
          color: getButtonTextColor(),
          backgroundColor: getButtonColor(),
        }}
      >
        {t("mentor_availability_reminder.cta")}
      </Button>
    </aside>,
    document.body,
  );
}
